import type { AdminWorkspaceSummary } from '@molt/shared';
import { Link } from '@tanstack/react-router';

import { RelativeTime } from '@/components/RelativeTime';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  ADMIN_TD_CLASS,
  ADMIN_TH_CLASS,
  ADMIN_THEAD_CLASS,
  ADMIN_TR_CLASS,
  AdminPill,
  AdminTableFrame,
} from './AdminConsole';

/**
 * SA-9 — the Workspaces tab: live workspaces only (SA-8), with the correlated
 * member and live-board counts the route computes.
 *
 * The name opens `/w/$workspaceId/members`, the page the shell's own "open this
 * workspace" lands on (`__root.tsx`). It renders for a platform superadmin who
 * holds no row there: `GET /workspaces/:id` carries the superadmin arm beside
 * its membership conjunct, and the member list sits on `requireWorkspaceMember`,
 * which admits them (SA-5, D10) — and every admin control on the page follows
 * (`w.$workspaceId.members.test.tsx`'s SA-9 block). There is no bare
 * `/w/$workspaceId` route to link instead.
 */

export interface AdminWorkspaceTableProps {
  readonly items: readonly AdminWorkspaceSummary[];
}

export function AdminWorkspaceTable({ items }: AdminWorkspaceTableProps) {
  const { t } = useInterfaceLanguage();
  return (
    <AdminTableFrame label={t('admin.workspaces.tableLabel')}>
      <thead className={ADMIN_THEAD_CLASS}>
        <tr>
          <th className={ADMIN_TH_CLASS}>{t('admin.workspaces.column.name')}</th>
          <th className={ADMIN_TH_CLASS}>{t('admin.workspaces.column.shortName')}</th>
          <th className={ADMIN_TH_CLASS}>{t('admin.workspaces.column.members')}</th>
          <th className={ADMIN_TH_CLASS}>{t('admin.workspaces.column.boards')}</th>
          <th className={ADMIN_TH_CLASS}>{t('admin.workspaces.column.enterprise')}</th>
          <th className={ADMIN_TH_CLASS}>{t('admin.workspaces.column.created')}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((workspace) => (
          <tr key={workspace.id} className={ADMIN_TR_CLASS}>
            <td className={ADMIN_TD_CLASS}>
              <Link
                to="/w/$workspaceId/members"
                params={{ workspaceId: workspace.id }}
                className="font-medium text-ink underline-offset-2 hover:underline"
              >
                {workspace.name}
              </Link>
            </td>
            <td className={`${ADMIN_TD_CLASS} text-ink-muted`}>{workspace.shortName}</td>
            <td className={`${ADMIN_TD_CLASS} tabular-nums text-ink`}>
              {workspace.memberCount}
            </td>
            <td className={`${ADMIN_TD_CLASS} tabular-nums text-ink`}>
              {workspace.boardCount}
            </td>
            <td className={ADMIN_TD_CLASS}>
              {workspace.enterpriseId === null ? (
                <span className="text-ink-muted">
                  {t('admin.workspaces.noEnterprise')}
                </span>
              ) : (
                <AdminPill>{t('admin.workspaces.inEnterprise')}</AdminPill>
              )}
            </td>
            <td className={`${ADMIN_TD_CLASS} whitespace-nowrap text-ink-muted`}>
              <RelativeTime value={workspace.createdAt} />
            </td>
          </tr>
        ))}
      </tbody>
    </AdminTableFrame>
  );
}
